// Purpose of this middleware is intercept Layout actions and check if those actions are dispatched has some package level interactions
// If yes, then this middleware will either dispatch a new action to the store or will augment the action and dispatch it to the store

import type { WidgetAddChild } from "actions/pageActions";
import type { ReduxAction } from "actions/ReduxActionTypes";
import { MAIN_CONTAINER_WIDGET_ID } from "constants/WidgetConstants";
import { getModuleInstanceByWidgetId } from "ee/selectors/moduleInstanceSelectors";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
  WidgetReduxActionTypes,
} from "ee/constants/ReduxActionConstants";
import { getCurrentPackageId } from "ee/selectors/packageSelectors";
import type { Middleware, MiddlewareAPI } from "redux";
import type { UpdateWidgetPropertyPayload } from "actions/controlActions";
import { MAIN_MODULE_CONTAINER_WIDGET_ID } from "ee/utils/Packages/createBaseModuleDSL";

interface UpdateWidgetNamePayload {
  id: string;
  newName: string;
}

const moduleHandler = (action: ReduxAction<unknown>) => {
  switch (action.type) {
    case ReduxActionTypes.UPDATE_LAYOUT:
      return {
        type: ReduxActionTypes.UPDATE_MODULE_LAYOUT_INIT,
        payload: action.payload,
      };
    case WidgetReduxActionTypes.WIDGET_ADD_CHILD:
      const { type, widgetId } = action.payload as unknown as WidgetAddChild;

      if (type === "MODAL_WIDGET") {
        return action;
      }

      // In UI module editor, widget not allowed to be added to the main container widget
      if (widgetId === MAIN_CONTAINER_WIDGET_ID) {
        return {
          type: ReduxActionErrorTypes.ADD_WIDGET_TO_MODULE_ERROR,
          payload: {
            error: {
              message:
                "Widgets are only allowed to be added to the ModuleContainer widget",
            },
          },
        };
      }

      return action;
    case ReduxActionTypes.UPDATE_APP_LAYOUT:
      // TODO: Currently, we are not handling the responsive layout for UI modules
      // We need to handle it in the future
      return {
        type: ReduxActionTypes.UPDATE_MODULE_RESPONSIVE_LAYOUT,
        payload: action.payload,
      };

    case ReduxActionTypes.BIND_WIDGET_TO_DATASOURCE:
      return {
        type: ReduxActionTypes.BIND_WIDGET_TO_DATASOURCE_ERROR,
        payload: {
          show: true,
          error: {
            message: "This action is not yet available for UI modules",
          },
        },
      };
    case ReduxActionTypes.BATCH_UPDATE_MULTIPLE_WIDGETS_PROPERTY:
      const { updatesArray } = action.payload as {
        updatesArray: UpdateWidgetPropertyPayload[];
      };

      const hasInputsFormUpdate = updatesArray.some((update) => {
        return (
          "inputsForm" in (update.updates.modify || {}) &&
          update.widgetId === MAIN_MODULE_CONTAINER_WIDGET_ID
        );
      });

      if (hasInputsFormUpdate) {
        return {
          type: ReduxActionTypes.UPDATE_UI_MODULE_INPUTS,
          payload: action.payload,
        };
      }

      const hasOutputsFormUpdate = updatesArray.some((update) => {
        return (
          "outputsForm" in (update.updates.modify || {}) &&
          update.widgetId === MAIN_MODULE_CONTAINER_WIDGET_ID
        );
      });

      if (hasOutputsFormUpdate) {
        return {
          type: ReduxActionTypes.UPDATE_UI_MODULE_OUTPUTS,
          payload: action.payload,
        };
      }

      return action;

    case ReduxActionTypes.WIDGETS_MOVE:
      const payload = action.payload as { canvasId: string };

      if (payload?.canvasId === MAIN_CONTAINER_WIDGET_ID) {
        return {
          type: ReduxActionErrorTypes.WIDGET_OPERATION_ERROR,
          payload: {
            action: ReduxActionTypes.WIDGETS_MOVE,
            error: {
              message:
                "Widgets are only allowed to be moved within the ModuleContainer widget",
            },
          },
        };
      }

      return action;
    default:
      return action;
  }
};

const moduleInstanceHandler = (
  action: ReduxAction<unknown>,
  store: MiddlewareAPI,
) => {
  switch (action.type) {
    case ReduxActionTypes.WIDGET_SINGLE_DELETE:
      store.dispatch({
        type: ReduxActionTypes.DELETE_UI_MODULE_INSTANCE_BY_WIDGET_INIT,
        payload: action.payload,
      });

      return action;
    case ReduxActionTypes.WIDGET_BULK_DELETE:
      store.dispatch({
        type: ReduxActionTypes.DELETE_BULK_UI_MODULE_INSTANCE_BY_WIDGET_INIT,
        payload: action.payload,
      });

      return action;
    case ReduxActionTypes.UPDATE_WIDGET_NAME_INIT:
      const { id, newName } = action.payload as UpdateWidgetNamePayload;
      const moduleInstance = getModuleInstanceByWidgetId(store.getState(), id);

      if (moduleInstance) {
        return {
          type: ReduxActionTypes.SAVE_MODULE_INSTANCE_NAME_INIT,
          payload: {
            id: moduleInstance.id,
            name: newName,
          },
        };
      }

      return action;
    default:
      return action;
  }
};

const PackageMiddleware: Middleware =
  (store: MiddlewareAPI) =>
  (next: (action: ReduxAction<unknown>) => void) =>
  (action: ReduxAction<unknown>) => {
    const currentPackageId = getCurrentPackageId(store.getState());

    if (currentPackageId) {
      const newAction = moduleHandler(action);

      return next(newAction);
    }

    const newAction = moduleInstanceHandler(action, store);

    return next(newAction);
  };

export default PackageMiddleware;
